import { Box, Stack, Typography } from '@mui/material'
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import { useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import authUtils from '../../utils/authUtils'
import Loading from '../common/Loading'
import Brand from '../common/Brand'
import { tokens } from '../../theme'

const highlights = [
  {
    icon: <ViewKanbanOutlinedIcon fontSize='small' />,
    title: 'Sections that keep their place',
    copy: 'Drag a task across columns and the order is saved as you drop it.'
  },
  {
    icon: <NotesOutlinedIcon fontSize='small' />,
    title: 'Notes on every card',
    copy: 'Each task holds a rich text note, so context lives next to the work.'
  },
  {
    icon: <BoltOutlinedIcon fontSize='small' />,
    title: 'One board per project',
    copy: 'Pin the boards you use daily and keep the rest out of the way.'
  }
]

const AuthLayout = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    authUtils.isAuthenticated().then((user) => {
      if (!active) return
      if (user) {
        navigate('/', { replace: true })
      } else {
        setLoading(false)
      }
    })
    return () => { active = false }
  }, [navigate])

  if (loading) return <Loading fullHeight />

  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' } }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          borderRight: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          overflow: 'hidden',
          backgroundImage:
            `radial-gradient(700px 420px at 15% 0%, ${tokens.accentSoft}, transparent 65%), radial-gradient(600px 480px at 100% 100%, rgba(110, 168, 254, 0.08), transparent 60%)`
        }}
      >
        <Brand />

        <Box sx={{ maxWidth: 460 }}>
          <Typography variant='h1' sx={{ mb: 2, textWrap: 'balance' }}>
            Every board has a next step.
          </Typography>
          <Typography color='text.secondary' sx={{ mb: 5, maxWidth: 380 }}>
            Organise work into sections, move tasks where they belong, and keep the details
            on the card instead of in your head.
          </Typography>

          <Stack spacing={3}>
            {highlights.map((item) => (
              <Stack key={item.title} direction='row' spacing={1.75} alignItems='flex-start'>
                <Box
                  sx={{
                    mt: 0.25,
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    color: 'primary.main',
                    backgroundColor: tokens.accentSoft
                  }}
                >
                  {item.icon}
                </Box>
                <Box>
                  <Typography variant='subtitle1'>{item.title}</Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {item.copy}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Typography variant='caption' color='text.disabled'>
          Boards, sections and tasks are private to your account.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, md: 6 },
          py: { xs: 6, md: 8 }
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, mb: 5 }}>
            <Brand />
          </Box>
          <Outlet />
        </Box>
      </Box>
    </Box>
  )
}

export default AuthLayout
